"use client";

import {
  useCallback,
  useEffect,
  useId,
  useRef,
  useState,
  type ReactNode,
} from "react";
import { usePathname } from "next/navigation";
import { ChevronDown } from "lucide-react";

type NavDropdownProps = {
  /** Visible trigger content. Pass a `<Link>` here for a label that also navigates. */
  label: ReactNode;
  children: ReactNode;
  /** Which edge the panel aligns to. Use "end" for right-hand menus. */
  align?: "start" | "end";
  panelClassName?: string;
  triggerClassName?: string;
  /** Accessible name for the toggle when `label` is a link or icon. */
  toggleLabel: string;
};

/**
 * Navbar dropdown that opens on click as well as hover.
 *
 * The navbar's menus used to be pure CSS `group-hover`, which meant they never
 * opened on any device without a real pointer - tablets and touch laptops get
 * the desktop navbar at >=xl but no `:hover`, so "Course categories", "More"
 * and the language switcher were simply dead there.
 *
 * Click/tap is therefore the primary interaction and works everywhere. Hover is
 * kept as a convenience but only for genuine mouse pointers: the handlers check
 * `pointerType`, so a touch never triggers the hover path and then immediately
 * fights the click. Escape, outside-click and route changes all close it, and
 * the trigger carries the aria state a menu button needs.
 */
export default function NavDropdown({
  label,
  children,
  align = "start",
  panelClassName = "",
  triggerClassName = "",
  toggleLabel,
}: NavDropdownProps) {
  const [open, setOpen] = useState(false);
  const containerRef = useRef<HTMLDivElement | null>(null);
  const panelId = useId();
  const pathname = usePathname();

  const close = useCallback(() => setOpen(false), []);

  // Navigating from inside the panel should not leave it hanging open over the
  // new page.
  useEffect(() => {
    close();
  }, [pathname, close]);

  useEffect(() => {
    if (!open) {
      return;
    }

    const handlePointerDown = (event: PointerEvent) => {
      if (!containerRef.current?.contains(event.target as Node)) {
        close();
      }
    };

    const handleKeyDown = (event: KeyboardEvent) => {
      if (event.key === "Escape") {
        close();
      }
    };

    document.addEventListener("pointerdown", handlePointerDown);
    document.addEventListener("keydown", handleKeyDown);

    return () => {
      document.removeEventListener("pointerdown", handlePointerDown);
      document.removeEventListener("keydown", handleKeyDown);
    };
  }, [open, close]);

  // Mouse only - a touch "hover" would open the panel and then have the
  // synthesised click close it again straight away.
  const handlePointerEnter = (event: React.PointerEvent) => {
    if (event.pointerType === "mouse") {
      setOpen(true);
    }
  };

  const handlePointerLeave = (event: React.PointerEvent) => {
    if (event.pointerType === "mouse") {
      setOpen(false);
    }
  };

  return (
    <div
      ref={containerRef}
      className="relative"
      onPointerEnter={handlePointerEnter}
      onPointerLeave={handlePointerLeave}
    >
      <div className={`flex items-center gap-2 ${triggerClassName}`}>
        {label}
        <button
          type="button"
          aria-label={toggleLabel}
          aria-expanded={open}
          aria-haspopup="menu"
          aria-controls={panelId}
          onClick={() => setOpen((v) => !v)}
          className="flex cursor-pointer items-center rounded p-0.5 focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-secondary"
        >
          <ChevronDown
            size={14}
            aria-hidden="true"
            className={`transition-transform duration-200 ${open ? "rotate-180" : ""}`}
          />
        </button>
      </div>

      <div
        id={panelId}
        role="menu"
        hidden={!open}
        className={`absolute top-full z-50 ${
          align === "end" ? "right-0" : "left-0"
        } ${panelClassName}`}
      >
        {children}
      </div>
    </div>
  );
}
